import { ChangeEvent, useState } from 'react';
import './MockSpCp.scss';
import classNames from 'classnames';
import { AuthAPI } from '@/services/api/auth';
import { saveAuthType } from '@/services/localService/localService';
import axios from 'axios';
import {
  LocalStorageService,
  maskNRIC,
  reportDtCustomError,
} from 'br2s-ui-common-utils-02';
import { useStore } from '@/redux/store';
import { checkIndicator } from '@/utilities/utilities';
import { toggleEntityChange } from '../ProfileSelection/profileSelection-utilities';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { flushSync } from 'react-dom';

const MockSpCp = () => {
  const [form, setForm] = useState({ nric: '', uen: '', uuid: '' });
  const [errors, setErrors] = useState({ nric: '', uen: '', uuid: '' });
  const [disabled, setDisabled] = useState(false);
  const { authenticate } = useStore();
  const [, setCookie] = useCookies(['isSessionActive', 'sessionType']);
  const navigate = useNavigate();

  const validateFields = (type: 'sp' | 'cp') => {
    setErrors({ nric: '', uen: '', uuid: '' });

    let isValid = true;

    Object.keys(form).forEach((k) => {
      if (type === 'sp' && k === 'uen') return;

      if ((form as Record<string, string>)[k] === '') {
        isValid = false;
        setErrors((e) => ({
          ...e,
          [k]: `Please enter a valid ${k.toUpperCase()}.`,
        }));
      }
    });

    return isValid;
  };

  const handleClick = (type: 'sp' | 'cp') => {
    if (!validateFields(type)) return;

    setDisabled(true);
    handleToken(type);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setForm((v) => ({ ...v, [e.target.name]: e.target.value }));
    setErrors((v) => ({ ...v, [e.target.name]: '' }));
  };

  const handleToken = async (type: 'sp' | 'cp') => {
    try {
      const res = await AuthAPI.getMockToken({
        uin: form.nric,
        uuid: form.uuid,
        loginType: type.toUpperCase() as 'SP' | 'CP',
        ...(type === 'cp' ? { uen: form.uen } : {}),
      });

      if (res.status === 200) {
        handleAuthScope(type);
      }
    } catch (error) {
      console.error(error);

      // istanbul ignore next
      if (axios.isAxiosError(error)) {
        reportDtCustomError(
          'AuthFailureError',
          'Mock SPCP - Token API failed.',
          `${error.response?.status}: ${error.response?.data.error?.errorDesc}`,
        );
      } else {
        reportDtCustomError('GenericHandledError', JSON.stringify(error));
      }

      setDisabled(false);
    }
  };

  const handleAuthScope = async (type: 'sp' | 'cp') => {
    try {
      const response = await AuthAPI.getSessionInfo();

      if (response?.status === 200) {
        const { result } = response.data;
        saveAuthType(type === 'sp' ? 'singpass' : 'corppass');
        LocalStorageService.setAuth(JSON.stringify(result));
        flushSync(() => {
          setCookie('isSessionActive', 'true', { path: '/' });
          setCookie('sessionType', 'authed', { path: '/' });
          authenticate();
        });
        handleNavigate(type);
        if (window.dtrum) {
          window.dtrum.identifyUser(maskNRIC(result.user?.uin));
        }
      }
    } catch (error) {
      console.error(error);

      // istanbul ignore next
      if (axios.isAxiosError(error)) {
        reportDtCustomError(
          'AuthFailureError',
          'Mock SPCP - Session info API failed.',
          `${error.response?.status}: ${error.response?.data.error?.errorDesc}`,
        );
      } else {
        reportDtCustomError('GenericHandledError', JSON.stringify(error));
      }
    } finally {
      setDisabled(false);
    }
  };

  const handleNavigate = (type: 'sp' | 'cp') => {
    const urlPriorToLogin = LocalStorageService.getUrlPriorToLogin();
    if (urlPriorToLogin) {
      navigate(urlPriorToLogin ?? '/dashboard');
      return;
    }

    // Check if auth type is singpass. If not, proceed to check indicators
    if (type === 'sp') {
      navigate(urlPriorToLogin ?? '/dashboard');
      return;
    }

    const hasYIndicator = checkIndicator();

    if (hasYIndicator) {
      navigate('/profile-selection');
      return;
    }

    navigate(urlPriorToLogin ?? '/dashboard');
    toggleEntityChange('false');
    LocalStorageService.clearUrlPriorToLogin();
  };

  return (
    <div className="mock-spcp">
      <div className="advisory-cont">
        <div className="advisory">
          <span className="advisory-title">Advisory Note</span>
          <span className="advisory-body">
            Your MockPass account contains a lot of personal data. Do not share
            your username, password and 2FA details with anyone.
          </span>
        </div>
      </div>
      <div className="main">
        <h1>Mockpass</h1>
        <div>
          <input
            type="text"
            placeholder="NRIC"
            name="nric"
            value={form.nric}
            onChange={handleChange}
            className={classNames({
              error: !!errors.nric,
            })}
          />
          <span>{errors.nric}</span>

          <input
            type="text"
            placeholder="UEN"
            name="uen"
            value={form.uen}
            onChange={handleChange}
            className={classNames({
              error: !!errors.uen,
            })}
          />
          <span>{errors.uen}</span>

          <input
            type="text"
            placeholder="UUID"
            name="uuid"
            value={form.uuid}
            onChange={handleChange}
            className={classNames({
              error: !!errors.uuid,
            })}
          />
          <span>{errors.uuid}</span>

          <button disabled={disabled} onClick={() => handleClick('sp')}>
            Singpass login
          </button>
          <button disabled={disabled} onClick={() => handleClick('cp')}>
            Corppass login
          </button>
        </div>
      </div>
    </div>
  );
};

export default MockSpCp;
